import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import {
	Box,
	Button,
	CircularProgress,
	Grid,
	IconButton,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TablePagination,
	TableRow,
	Typography,
} from "@mui/material";

import { CustomDate } from "components";
import CustomTableHead from "components/CustomTableHead";
import Dialog from "components/Dialog";

import { Collapse, Expand, Info } from "images";

import { POSITIONS_HEAD_CELLS } from "./Constants";
import { Pair, Price, Profit, Reason, Side } from "./columns";
import PercentChange from "./columns/PercentChange";
import { QuoteSymbolMap, calculatePriceChange } from "./utils";
import { roundOff } from "./utils/NumberHelper";

const DetailField = ({ value, label }) => (
	<Grid item>
		<Typography sx={{ fontSize: "12px", fontWeight: 700 }}>{label}</Typography>
		<Typography
			sx={{
				fontSize: "16px",
				fontWeight: 700,
				color: (theme) => theme.palette.primary.main,
			}}
		>
			{value}
		</Typography>
	</Grid>
);

export default function Positions({
	positions,
	filter,
	count,
	onPageChange,
	onRowsPerPageChange,
	hideLookup,
	onOrderChange,
}) {
	const [expand, setExpand] = useState(positions?.map((x) => false));
	const [open, setOpen] = useState(false);
	const [selectedErrorLog, setSelectedErrorLog] = useState();

	const { t } = useTranslation("controlPanel");
	const navigate = useNavigate();

	const { price: priceMap } = useSelector((state) => state.price);

	useEffect(() => {
		if (positions?.length) {
			setExpand(positions.map((_) => false));
		}
	}, [positions]);

	return (
		<>
			{positions ? (
				<TableContainer>
					<Table sx={{ minWidth: 750, borderCollapse: "separate", borderSpacing: "0 0.75rem" }}>
						<CustomTableHead
							expand
							headCells={POSITIONS_HEAD_CELLS(t, filter?.where?.status, hideLookup, onOrderChange)}
							order={filter?.orderBy}
							onOrderChange={onOrderChange}
						/>
						{positions.length <= 0 ? (
							<TableBody
								sx={{
									backgroundColor: "#fff",
									borderRadius: "8px",
								}}
							>
								<TableRow>
									<TableCell
										colSpan={POSITIONS_HEAD_CELLS(t, filter?.where?.status, hideLookup).length + 1}
									>
										<Typography sx={{ textAlign: "center" }}>
											{filter?.where?.status === "OPEN"
												? t("control_panel_positions_table_no_open_positions")
												: filter?.where?.status === "ERROR"
												? t("control_panel_positions_table_no_closed_positions_control_logs")
												: t("control_panel_positions_table_no_closed_positions")}
										</Typography>
									</TableCell>
								</TableRow>
							</TableBody>
						) : (
							<TableBody
								sx={{
									backgroundColor: filter?.where?.status === "OPEN" ? "white" : "#F0F0F5",
									mt: 4,
								}}
							>
								{positions?.map((item, index) => {
									const {
										market_strategy,
										merchant_nickname: merchantName,
										parity,
										platform,
										strategy,
										trading_services_error_log,
									} = item;

									const { execution_type, public: strategyPublic } = strategy;
									const strategyName = market_strategy?.name ?? strategy?.name;

									const { name: platformName, info } = platform;
									const { platformKey } = info;

									const {
										amountExecuted,
										enterPrice,
										enterDate,
										exitDate,
										exitPrice,
										drawUp,
										drawDown,
										profitInQuote,
										size,
									} = item.position_info;

									const { leverage } = item.position_settings;

									const enterAmount = amountExecuted * enterPrice;

									const maximumDrawUp = drawUp?.price
										? (
												(execution_type === "SHORT" ? -1 : 1) *
												((drawUp?.price - enterPrice) / enterPrice) *
												(leverage ?? 1) *
												100
										  ).toFixed(2)
										: 0;
									const maximumDrawDown = drawDown?.price
										? (
												(execution_type === "SHORT" ? -1 : 1) *
												((drawDown?.price - enterPrice) / enterPrice) *
												(leverage ?? 1) *
												100
										  ).toFixed(2)
										: 0;

									const priceChange = calculatePriceChange(item, priceMap);

									const profitAmount =
										filter?.where?.status === "OPEN"
											? (size?.inQuote / (100 * (leverage ?? 1))) * priceChange
											: profitInQuote;

									return (
										<React.Fragment key={index}>
											<TableRow key={index}>
												{!hideLookup?.["pair"] && (
													<TableCell>
														<Pair parity={parity} />
													</TableCell>
												)}
												{!hideLookup?.["marketType"] && (
													<TableCell>
														<Side label={execution_type} />
													</TableCell>
												)}
												{!hideLookup?.["exchange"] && <TableCell>{platformName}</TableCell>}
												<TableCell>
													<CustomDate date={enterDate} />
												</TableCell>
												{filter?.where?.status === "CLOSED" && (
													<TableCell>
														<CustomDate date={exitDate} />
													</TableCell>
												)}
												{!hideLookup?.["expertStrategy"] && (
													<TableCell>
														<Typography
															fontWeight={500}
															sx={{
																cursor: "pointer",
															}}
															onClick={() =>
																strategy.public === 1 &&
																navigate(
																	`/marketplace/merchant/${strategy?.market_strategy.merchantId}`
																)
															}
														>
															{strategyPublic
																? merchantName
																: t("control_panel_positions_table_own_strategy")}
														</Typography>
														<Typography
															sx={{
																cursor: "pointer",
															}}
															onClick={() =>
																strategy.public === 1 &&
																navigate(`/marketplace/strategy/${market_strategy.id}`)
															}
														>
															{strategyName}
														</Typography>
													</TableCell>
												)}
												{filter?.where?.status !== "ERROR" && (
													<>
														<TableCell>
															<Profit
																platformId={platform.id}
																quote={parity?.quote}
																profitAmount={profitAmount}
																virtual={item.virtual}
															/>
														</TableCell>
														<TableCell>
															<Price
																platformId={platform.id}
																quote={parity?.quote}
																price={
																	filter?.where?.status === "CLOSED"
																		? roundOff(exitPrice)
																		: priceMap?.[`${platformKey}-${parity.symbol}`]
																}
																priceChange={priceChange}
															/>
														</TableCell>
													</>
												)}
												{filter?.where?.status === "CLOSED" && (
													<TableCell>
														<Typography>{item.position_info.exitBy}</Typography>
													</TableCell>
												)}
												{filter?.where?.status !== "ERROR" && (
													<TableCell>
														<IconButton
															sx={{
																"&:hover": {
																	backgroundColor: "inherit",
																},
															}}
															onClick={() => {
																setExpand([
																	...expand.slice(0, index),
																	!expand[index],
																	...expand.slice(index + 1),
																]);
															}}
														>
															{expand?.[index] ? <Collapse /> : <Expand />}
														</IconButton>
													</TableCell>
												)}
												{filter?.where?.status === "ERROR" && (
													<>
														<TableCell>
															<CustomDate date={trading_services_error_log?.created_at} />
														</TableCell>
														<TableCell>
															<Reason
																text={
																	trading_services_error_log?.paratica_error_code
																		? t(
																				`error:tradingServicesErrorLog.reason.${trading_services_error_log?.paratica_error_code}`
																		  )
																		: t(`error:tradingServicesErrorLog.reason.500`)
																}
															/>
														</TableCell>
														{t(
															`error:tradingServicesErrorLog.comment.${trading_services_error_log?.paratica_error_code}`
														) && (
															<TableCell>
																<IconButton
																	onClick={() => {
																		setSelectedErrorLog(trading_services_error_log);
																		setOpen(true);
																	}}
																>
																	<Info />
																</IconButton>
															</TableCell>
														)}
													</>
												)}
											</TableRow>
											{expand?.[index] && (
												<TableRow>
													<TableCell
														sx={{
															backgroundColor: (theme) =>
																theme.palette.background.default,
															border: "none",
														}}
														colSpan={
															POSITIONS_HEAD_CELLS(t, filter?.where?.status).length + 1
														}
													>
														<Grid
															container
															spacing={2}
															sx={{
																p: "8px",
																ml: "8px",
																width: "99%",
																borderRadius: "8px",
																backgroundColor: "#F4F5FD",
															}}
														>
															<DetailField
																label={t("control_panel_positions_table_enter_price")}
																value={`$${roundOff(enterPrice)}`}
															/>

															{!item?.virtual && enterAmount && (
																<DetailField
																	label={t(
																		"control_panel_positions_table_enter_amount"
																	)}
																	value={`${
																		platform.id === 1 && parity?.quote === "USD"
																			? "₿"
																			: QuoteSymbolMap[parity?.quote] ?? "$"
																	}${roundOff(size.inQuote)}`}
																/>
															)}

															{!item?.virtual &&
																size?.inUSDT !== size?.inQuote &&
																parity?.quote !== "USDT" && (
																	<DetailField
																		label={t("Position Size (USDT)")}
																		value={`${"$"}${roundOff(size?.inUSDT)}`}
																	/>
																)}
															<DetailField
																label={t("control_panel_positions_table_leverage")}
																value={`${execution_type}${
																	leverage ? " x " + leverage : ""
																}`}
															/>

															<PercentChange
																label={t("control_panel_positions_table_max_du")}
																value={maximumDrawUp}
															/>
															<PercentChange
																label={t("control_panel_positions_table_max_dd")}
																value={maximumDrawDown}
															/>
														</Grid>
													</TableCell>
												</TableRow>
											)}
										</React.Fragment>
									);
								})}
							</TableBody>
						)}
					</Table>
				</TableContainer>
			) : (
				<CircularProgress />
			)}
			{open && (
				<Dialog
					dialogProps={{
						open,
						maxWidth: "lg",
						onClose: () => {
							setOpen();
							setSelectedErrorLog();
						},
					}}
					title={
						selectedErrorLog?.paratica_error_code
							? t(`error:tradingServicesErrorLog.reason.${selectedErrorLog?.paratica_error_code}`)
							: t(`error:tradingServicesErrorLog.reason.500`)
					}
					content={
						<Typography>
							{t(`error:tradingServicesErrorLog.comment.${selectedErrorLog?.paratica_error_code}`)}
						</Typography>
					}
					action={
						<Button variant="contained" fullWidth onClick={() => setOpen()}>
							Ok
						</Button>
					}
				/>
			)}
			{filter && count ? (
				<Box sx={{ display: "flex", justifyContent: "end", width: "100%" }}>
					<TablePagination
						labelRowsPerPage={t("common:table_pagination_text")}
						labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
						rowsPerPageOptions={onRowsPerPageChange ? [5, 10, 25, 50, 100] : []}
						component="div"
						count={count}
						rowsPerPage={filter?.where?.pageSize}
						page={filter?.where?.pageNumber}
						onPageChange={onPageChange}
						onRowsPerPageChange={onRowsPerPageChange}
					/>
				</Box>
			) : (
				<> </>
			)}
		</>
	);
}
